import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { ArrowLeft, Calendar, Clock, ArrowRight } from "lucide-react";
import { BLOG_POSTS, formatDate } from "@/pages/BlogPage";
import { Logo } from "@/pages/LandingPage";
import WhatsAppButton, { MobileQuickBar } from "@/components/WhatsAppButton";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (post) document.title = `${post.title} — SolveDesk Journal`;
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0" data-testid="blog-post-page">
      <Seo
        title={`${post.title} — SolveDesk Journal`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
      />
      <WhatsAppButton />
      <MobileQuickBar />
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="post-logo-link"><Logo /></Link>
          <Link to="/blog" data-testid="post-back-link"
            className="text-sm font-medium text-slate-600 hover:text-primary transition-colors duration-200 flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> All Articles
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <motion.article initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-muted-foreground mb-6">
            <span className="rounded-full bg-accent px-3 py-1 text-accent-foreground font-semibold">{post.category}</span>
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(post.date)}</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.readTime}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-slate-900 leading-tight" data-testid="post-title">
            {post.title}
          </h1>
          <div className="mt-10 space-y-6" data-testid="post-body">
            {post.body.map((para, i) => (
              <p key={i} className="text-base sm:text-lg text-slate-600 leading-relaxed">{para}</p>
            ))}
          </div>

          <div className="mt-16 rounded-3xl bg-slate-900 p-8 sm:p-10 text-center" data-testid="post-cta">
            <h3 className="font-display text-2xl font-semibold text-white">Ready to get your virtual office?</h3>
            <p className="mt-3 text-white/70 text-sm sm:text-base">
              Prime addresses across India. GST-ready documents in days. No expensive lease.
            </p>
            <Link to="/#inquire">
              <Button data-testid="post-cta-button"
                className="mt-6 rounded-full bg-primary hover:bg-primary/90 text-white px-8 h-12 font-semibold shadow-lg shadow-primary/25 transition-transform duration-200 hover:-translate-y-0.5">
                Inquire Now <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </motion.article>
      </main>

      <footer className="border-t border-slate-100 py-8 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Logo />
          <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} solvedesk.space</p>
        </div>
      </footer>
    </div>
  );
}
